import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type WebhookDispatchRow } from '../../lib/api'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Card, CardContent } from '../../components/ui/Card'

export function Webhooks() {
  const { t } = useTranslation()
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'webhooks'],
    queryFn: () => api<WebhookDispatchRow[]>('/api/v1/webhooks'),
  })

  const retry = useMutation({
    mutationFn: (id: string) =>
      api(`/api/v1/webhooks/${id}/retry`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'webhooks'] }),
  })

  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-bold text-ink">{t('admin.webhooks.title')}</h1>

      {isLoading && <p className="text-sm text-text-muted">{t('common.loading')}</p>}

      {data && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-cream text-xs uppercase text-text-muted">
                <tr>
                  <th className="px-4 py-2 text-left">{t('admin.webhooks.columns.brief')}</th>
                  <th className="px-4 py-2 text-left">{t('admin.webhooks.columns.url')}</th>
                  <th className="px-4 py-2 text-left">{t('admin.webhooks.columns.status')}</th>
                  <th className="px-4 py-2 text-left">{t('admin.webhooks.columns.attempts')}</th>
                  <th className="px-4 py-2 text-left">{t('admin.webhooks.columns.last')}</th>
                  <th className="px-4 py-2 text-left">{t('admin.webhooks.columns.response')}</th>
                  <th className="px-4 py-2 text-left">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((w) => (
                  <tr key={w.id} className="border-t border-warm-mist">
                    <td className="px-4 py-2 font-mono text-xs">{w.brief_id.slice(0, 8)}</td>
                    <td className="px-4 py-2 text-xs">{w.url}</td>
                    <td className="px-4 py-2">
                      <Badge
                        tone={
                          w.status === 'delivered'
                            ? 'green'
                            : w.status === 'failed'
                              ? 'red'
                              : 'yellow'
                        }
                      >
                        {w.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-xs">{w.attempts}</td>
                    <td className="px-4 py-2 text-xs text-text-muted">
                      {w.last_attempted_at?.slice(0, 19).replace('T', ' ') ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-xs">{w.response_status ?? '—'}</td>
                    <td className="px-4 py-2">
                      {w.status !== 'delivered' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => retry.mutate(w.id)}
                        >
                          {t('common.retry')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {data.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-sm text-text-muted">
                      {t('common.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
